import { combineEpics } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import * as z from 'zod';
import { apiUrl } from '../../../constants/config';
import { organizationBranding } from '../../../features/client-config/organization/branding';
import { errorHandler } from '../../../lib/api';
import { parseJsonPreprocessor } from '../../../lib/parseJsonPreprocessor';
import {
  isNonEmptyString,
  isNotNullOrUndefined,
} from '../../../lib/typeGuards';
import { Organization } from '../../../types/organization';
import {
  MEMBERSHIP_ID,
  removeItems,
  setItem,
  storedUserData,
} from '../localStorage/actions';
import { throwInfo } from '../messages/actions';
import { fetchUserFulfilled } from '../user/actions';
import { isActionOf, makeEpic } from '../utils';
import {
  fetchOrganizationFulfilled,
  setOrganizationFont,
  updateOrganization,
  updateOrganizationFulfilled,
} from './actions';
import { loadFont, loadUnleashFlags } from './helpers';
import { safeOrganizationSelector } from './selectors';

const assetsMismatchSchema = z.object({
  reloadCount: z.number(),
  failedUrl: z.string(),
  timeStamp: z.string(),
});

const fetchOrganizationEpic = makeEpic((action$, _state$, { authGetJSON$ }) =>
  action$.pipe(
    filter(isActionOf(fetchUserFulfilled)),
    mergeMap(user =>
      authGetJSON$<{ results: Organization[] }>(`${apiUrl}/organizations`, {
        excludeKeysFromConversion: ['uiSettings', 'ui_settings'],
      }).pipe(
        map(({ results }) => results[0]),
        filter(isNotNullOrUndefined),
        mergeMap(organization =>
          from(loadUnleashFlags(organization)).pipe(
            mergeMap(() => {
              const savedMembershipId = localStorage.getItem(MEMBERSHIP_ID);

              const currentMembershipId = `${organization.id}-${user.payload.email}`;

              if (window.Sentry) {
                window.Sentry.setTags({
                  'organization.id': organization.id,
                  'organization.name': organization.name,
                });

                const result = z
                  .preprocess(parseJsonPreprocessor, assetsMismatchSchema)
                  .safeParse(localStorage.getItem('assets_mismatch'));

                if (result.success && result.data.reloadCount > 0) {
                  window.Sentry.withScope(scope => {
                    scope.setExtras({
                      reloadCount: result.data.reloadCount,
                      failedUrl: result.data.failedUrl,
                    });
                    scope.captureMessage('Assets Mismatch Detected');
                  });

                  localStorage.removeItem('assets_mismatch');
                }
              }

              if (!savedMembershipId)
                return of(
                  removeItems(storedUserData),
                  fetchOrganizationFulfilled(organization),
                  setItem(MEMBERSHIP_ID, currentMembershipId)
                );
              if (savedMembershipId !== currentMembershipId)
                return of(
                  removeItems(storedUserData),
                  fetchOrganizationFulfilled(organization)
                );

              return of(fetchOrganizationFulfilled(organization));
            })
          )
        ),
        catchError(errorHandler)
      )
    )
  )
);

const updateOrganizationEpic = makeEpic((action$, _, { authPatch$ }) =>
  action$.pipe(
    filter(isActionOf(updateOrganization)),
    mergeMap(({ payload }) => {
      return authPatch$<Organization>(
        `${apiUrl}/organizations/${payload.id}`,
        payload,
        { excludeKeysFromConversion: ['uiSettings', 'ui_settings'] }
      ).pipe(
        map(_organization => updateOrganizationFulfilled(_organization)),
        catchError(errorHandler)
      );
    })
  )
);

const updateOrganizationFulfilledEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(updateOrganizationFulfilled)),
    mergeMap(() => {
      throwInfo('organizationUpdated');
      return of({ type: 'EMPTY_ACTION' });
    })
  )
);

const setOrganizationFontEpic = makeEpic((_action$, state$) =>
  state$.pipe(
    map(state => safeOrganizationSelector(state)),
    filter(org => !!org),
    map(org => organizationBranding.fontFamily(org.uiSettings)),
    filter(isNonEmptyString),
    take(1),
    tap(font => {
      document.body.style.fontFamily = font;
    }),
    tap(font => loadFont(font)),
    map(font => setOrganizationFont(font))
  )
);

export default combineEpics(
  fetchOrganizationEpic,
  updateOrganizationEpic,
  updateOrganizationFulfilledEpic,
  setOrganizationFontEpic
);
